<script setup>
import {watch, ref, computed} from "vue";
import MeetingType from "@/components/common/input/meetingType.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import CommitteeSelectBox from "@/components/common/input/committeeSelectBox.vue";
import MeetingRoomSelectBox from "@/components/common/input/meetingRoomSelectBox.vue";
import {useLanguageStore} from "@/pinia";
import axios from "@/axios";
import {formatDate} from "@/plugins/dayjs";
import {useToast} from "@/composables/useToast";
const toast = useToast();
const languageStore = useLanguageStore();

const ageTitle = ref("");
const sessionId = ref("");
const meetingPreDt = ref("");
const meetingType = ref();
const committee = ref("");
const meetingLocation = ref("");
const memo = ref("");
const liveId = ref("");
const regDt = ref("");
const regUser = ref("");
const modDt = ref("");
const modUser = ref("");
const isModify = ref(false);
const isPrivate = ref(null);
const today = new Date();
const aboutVideoModal = ref(false);
const videos = ref();

// timezone 적용된 날짜 표시
const formattedRegDt = computed(() => formatDate(regDt.value, "YYYY-MM-DD HH:mm:ss"));
const formattedModDt = computed(() => formatDate(modDt.value, "YYYY-MM-DD HH:mm:ss"));

const set_aboutVideoModal = (data)=>{
  aboutVideoModal.value = data
}
const emits = defineEmits([
    'set_isDetailMeeting',
    'fn_modifyMeeting'
])
const props = defineProps([
    'meeting',
    'isDetailMeeting'
])
const set_meetingLocation = (ml)=>{
  meetingLocation.value =ml;
}
const set_meetingType = (ms)=>{
  meetingType.value = ms;
}
const getVideoAboutMeeting = (meetingId)=>{
  axios.get("/api/video/about-meeting/" + meetingId)
      .then(s=>{
        videos.value = s.data
      })
}
const fn_modify = ()=>{
  if(!meetingPreDt.value) {
    toast.warning(languageStore.get('fillInBlank'));
    return;
  }
  if(!meetingType.value) {
    toast.warning(languageStore.get('fillInBlank'));
    return;
  }
  if(!meetingLocation.value) {
    toast.warning(languageStore.get('fillInBlank'));
    return;
  }
  let data;
  if(meetingType.value == "mt001") {
    data = {
      "meetingId": props.meeting.meetingId,
      "age": {"ageId": props.meeting.age.ageId, "ageTitle": ageTitle.value},
      "sessionId": sessionId.value,
      "meetingPreDt": meetingPreDt.value,
      "meetingType": {"codeId": meetingType.value},
      "committeeId": {"codeId": committee.value},
      "meetingLocation": {"codeId": meetingLocation.value},
      "memo": memo.value,
      "liveId": liveId.value,
      "regDt": regDt.value,
      "regUser": regUser.value,
      "modDt": modDt.value,
      "modUser": modUser.value,
      "isPrivate" : isPrivate.value
    }
  }else{
    data = {
      "meetingId": props.meeting.meetingId,
      "age": {"ageId": props.meeting.age.ageId, "ageTitle": ageTitle.value},
      "sessionId": sessionId.value,
      "meetingPreDt": meetingPreDt.value,
      "meetingType": {"codeId": meetingType.value},
      "meetingLocation": {"codeId": meetingLocation.value},
      "memo": memo.value,
      "liveId": liveId.value,
      "regDt": regDt.value,
      "regUser": regUser.value,
      "modDt": modDt.value,
      "modUser": modUser.value,
      "isPrivate" : isPrivate.value
    }
  }

  emits('fn_modifyMeeting', data);
  close()
}
const set_isModify = (is)=>{
  isModify.value = is;
  // 취소 시 원래 데이터로 복원
  if(!is && props.meeting) {
    const m = props.meeting;
    ageTitle.value = m.age != null ? m.age.ageTitle : "";
    sessionId.value = m.sessionId ?? '';
    meetingPreDt.value = m.meetingPreDt ?? "";
    meetingType.value = m.meetingType != null ? m.meetingType.codeId : "";
    committee.value = m.committeeId != null ? m.committeeId.codeId : "";
    meetingLocation.value = m.meetingLocation != null ? m.meetingLocation.codeId : "";
    memo.value = m.memo ?? "";
    liveId.value = m.liveId ?? "";
    isPrivate.value = m.isPrivate ?? null;
  }
}
const close = ()=>{
  ageTitle.value = "";
  sessionId.value = "";
  meetingPreDt.value = "";
  meetingType.value = "";
  committee.value = "";
  meetingLocation.value = "";
  memo.value = "";
  liveId.value = "";
  regDt.value = "";
  regUser.value = "";
  modDt.value = "";
  modUser.value = "";
  isModify.value = false;
  isPrivate.value = null ;
  videos.value = null;
  emits('set_isDetailMeeting', false);
}

watch(() => props.meeting, (m) => {
  if(m != null){
    ageTitle.value = m.age != null ? m.age.ageTitle : "";
    sessionId.value =  m.sessionId ?? '';
    meetingPreDt.value = m.meetingPreDt ?? "";
    meetingType.value = m.meetingType != null ? m.meetingType.codeId  :"";
    committee.value = m.committeeId != null ? m.committeeId.codeId : "";
    meetingLocation.value = m.meetingLocation != null ? m.meetingLocation.codeId  :"";
    memo.value = m.memo ?? "";
    regDt.value = m.regDt ?? "";
    regUser.value = m.regUser ?? '';
    modDt.value = m.modDt ?? '';
    modUser.value = m.modUser ?? '';
    liveId.value = m.liveId ?? "";
    isPrivate.value = m.isPrivate ?? null;

    getVideoAboutMeeting(m.meetingId);
  }
}, { immediate: true })
const set_committee = (s_committee)=>{
  committee.value = s_committee;
}
</script>

<template>
  <v-dialog
      :model-value="isDetailMeeting"
      @update:model-value="close"
      width="auto"
  >
    <v-card width="700" :title="languageStore.get('meetingDetail')">
      <template v-slot:append>
        <v-btn class="aboutModal" variant="outlined" size="small" @click="set_aboutVideoModal(true)">{{ languageStore.get('viewRelatedVideos') }}</v-btn>
        <v-dialog v-model="aboutVideoModal" width="500" overlay>
          <v-card :title="languageStore.get('relatedVideos')">
            <v-card-text>
              <template v-if="videos && videos.length >0">
                <template v-for="video in videos" :key="video.id">
                  <div class="videoArea">
                    <div class="timezone">{{formatDate(video.regDt, "YYYY-MM-DD HH:mm")}}</div>
                    <div class="videoTitle">{{video.videoTitle}}</div>
                    <div class="videoMemo">{{video.videoDescription}}</div>
                  </div>
                </template>
              </template>
              <div v-else class="noData">
                {{languageStore.get('noData')}}
              </div>
            </v-card-text>
            <v-card-actions class="modal-actions">
              <v-spacer></v-spacer>
              <v-btn variant="outlined" @click="aboutVideoModal = false">{{ languageStore.get('close') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>

      <v-card-text>
        <div v-if="!isModify" class="view-mode-banner">
          <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
          {{ languageStore.get('editModeGuide') }}
        </div>
        <v-row>
          <v-col cols="6">
            <v-text-field
                v-model="ageTitle"
                :label="languageStore.get('number')"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                :disabled="isModify"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
                v-model="sessionId"
                :label="languageStore.get('meetingRound')"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :readonly="!isModify"
            />
          </v-col>
        </v-row>

        <vue-date-picker
            class="dp-full-width mb-4 mt-4"
            :readonly="!isModify"
            locale="ko"
            v-model="meetingPreDt"
            :placeholder="languageStore.get('scheduledDate')"
            :min-date="today"
            :is-24="false"
            :teleport="true"
        />

        <v-row>
          <v-col cols="5">
            <meetingType
                :readonly="!isModify"
                v-model="meetingType"
                :meeting="meetingType"
                @set_meetingType="set_meetingType"
                :label="languageStore.get('meetingType')"
            />
          </v-col>
          <v-col cols="7" v-if="meetingType == 'mt001'">
            <committee-select-box
                :committee="committee"
                :readonly="!isModify"
                @set_committee="set_committee"
                :label="languageStore.get('committee')"
            />
          </v-col>
        </v-row>

        <meeting-room-select-box
            :readonly="!isModify"
            v-model="meetingLocation"
            :meeting-room="meetingLocation"
            @set_meetingRoom="set_meetingLocation"
            :label="languageStore.get('meetingPlace')"
            class="mb-4"
        />

        <v-textarea
            :readonly="!isModify"
            v-model="memo"
            :label="languageStore.get('memo')"
            variant="outlined"
            density="compact"
            rows="2"
            hide-details="auto"
            class="mb-4"
        />

        <v-text-field
            :readonly="!isModify"
            v-model="liveId"
            :label="languageStore.get('liveId')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            class="mb-4"
        />

        <v-row>
          <v-col cols="6">
            <v-text-field
                :disabled="isModify"
                readonly
                :model-value="formattedRegDt"
                :label="languageStore.get('createdDate')"
                variant="outlined"
                density="compact"
                hide-details="auto"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
                :disabled="isModify"
                readonly
                v-model="regUser"
                :label="languageStore.get('createdBy')"
                variant="outlined"
                density="compact"
                hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-row v-if="modDt || modUser">
          <v-col cols="6">
            <v-text-field
                :disabled="isModify"
                readonly
                :model-value="formattedModDt"
                :label="languageStore.get('modifiedDate')"
                variant="outlined"
                density="compact"
                hide-details="auto"
            />
          </v-col>
          <v-col cols="6">
            <v-text-field
                :disabled="isModify"
                readonly
                v-model="modUser"
                :label="languageStore.get('modifiedBy')"
                variant="outlined"
                density="compact"
                hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-switch
            :readonly="!isModify"
            v-model="isPrivate"
            :label="languageStore.get('publicStatus')"
            color="primary"
            hide-details
            density="compact"
        />
      </v-card-text>

      <v-card-actions class="modal-actions">
        <v-spacer></v-spacer>
        <template v-if="isModify">
          <v-btn color="primary" variant="flat" @click="fn_modify">{{ languageStore.get('save') }}</v-btn>
          <v-btn variant="outlined" @click="set_isModify(false)">{{ languageStore.get('cancel') }}</v-btn>
        </template>
        <template v-else>
          <v-btn color="primary" variant="flat" prepend-icon="mdi-pencil" @click="set_isModify(true)">{{ languageStore.get('edit') }}</v-btn>
        </template>
        <v-btn variant="outlined" @click="close">{{ languageStore.get('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 스타일 */
:deep(.v-card-item) {
  background-color: #1A3A5C !important;
  padding: 0 !important;
}

:deep(.v-card-title) {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px !important;
  font-weight: 600 !important;
  font-size: 18px !important;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text) {
  padding: 24px !important;
}

/* 모달 액션 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-actions) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}

/* v-card-text 내 입력 필드 간격 */
.v-card-text :deep(.v-input),
.v-card-text :deep(.dp-full-width) {
  margin-bottom: 16px;
}

/* v-row 내부의 v-input은 margin 제외 */
.v-card-text :deep(.v-row .v-input) {
  margin-bottom: 0;
}

.v-card-text :deep(.v-row) {
  margin-top: 0;
  margin-bottom: 16px;
}

.v-card-text :deep(.v-col) {
  padding-top: 0;
  padding-bottom: 0;
}

.v-card-text :deep(.v-row:last-child),
.v-card-text :deep(.switch-field) {
  margin-bottom: 0;
}

/* DatePicker 스타일 */
.dp-full-width {
  width: 100%;
}

.dp-full-width :deep(.dp__input) {
  height: 40px;
  border-radius: 4px;
  border-color: #C7C7C7;
}

.videoArea {
  border: 1px solid #d7d7d7;
  border-radius: 1rem;
  padding: 1rem;
  margin: 1rem;
}

.timezone {
  font-size: 12px;
  color: #052C65;
}

.noData {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  color: #90A4AE;
  font-size: 14px;
  gap: 8px;
}
.aboutModal{
  color: white;
  margin-right: 10px;
}

.view-mode-banner {
  background-color: #E8F0FE;
  color: #1A3A5C;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 13px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

</style>